import React from 'react';
import { useTranslation } from 'react-i18next';
import { Site, SiteArea } from 'graphql-common';
import { ErrorLike } from '@apollo/client';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import Table from '@lib/components/Table/Table';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import DashboardContentMaxWidth from '@lib/layouts/DashboardLayout/DashboardContentMaxWidth/DashboardContentMaxWidth';
import Search from '@lib/components/Search/Search';
import { materialSymbolsOutlined } from '@lib/enums/common';
import ListEmptyState from '@lib/components/ListEmptyState/ListEmptyState';
import { NavItem } from '@lib/components/Menu/Menu';
import useSearchParam from '@lib/hooks/useSearchParam';
import { Column } from '@lib/components/Table/types';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import getStatusFromError from '@lib/utils/getStatusFromError';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import { UseBulkSelectResult } from '@lib/hooks/useTableItemsBulkSelect';
import DashboardTitleHeader from '@lib/layouts/DashboardLayout/DashboardTitleHeader/DashboardTitleHeader';
import ViewOnlyChip from '@lib/components/ViewOnlyChip/ViewOnlyChip';
import { ListQueryHookResult } from 'utils/fetch/types';

interface Props {
  openSiteAreaCreateModal: () => void;
  siteAreaError?: ErrorLike;
  siteData: Site;
  siteError?: ErrorLike;
  siteLoading?: boolean;
  tableActionsForSelected?: React.ReactNode[];
  tableActionsMenu?: NavItem<SiteArea>[];
  tableColumns: Column<SiteArea>[];
  useBulkSelectResult: UseBulkSelectResult<SiteArea>;
  isViewOnly: boolean;
  siteAreasQueryHookResult: ListQueryHookResult<SiteArea>;
}

function SiteAreas(props: Props) {
  const {
    isViewOnly,
    openSiteAreaCreateModal,
    siteAreaError,
    siteAreasQueryHookResult,
    siteData,
    siteError,
    siteLoading,
    tableActionsForSelected,
    tableActionsMenu,
    tableColumns,
    useBulkSelectResult,
  } = props;
  const { t } = useTranslation();
  const [searchQuery] = useSearchParam();

  if (siteLoading) {
    return <CircleLoader />;
  }

  if (siteAreasQueryHookResult.error) {
    return (
      <ErrorPage
        status={getStatusFromError(siteAreasQueryHookResult.error)}
        description={getDescriptionFromError(siteError)}
      />
    );
  }

  if (siteAreaError) {
    return (
      <ErrorPage
        status={getStatusFromError(siteAreaError)}
        description={getDescriptionFromError(siteAreaError)}
      />
    );
  }

  const emptyState = (
    <ListEmptyState
      title={t('no-arias-yet')}
      text={t('no-arias-text')}
      actions={
        isViewOnly
          ? []
          : [
              <Button
                key="add"
                buttonText={t('create-site-area')}
                buttonType={ButtonTypes.primaryFilled}
                leftIcon="add"
                onClick={openSiteAreaCreateModal}
              />,
            ]
      }
    />
  );

  return (
    <DashboardContentMaxWidth>
      <WhiteBox>
        <DashboardTitleHeader
          title={siteData?.name}
          actions={
            isViewOnly ? (
              <ViewOnlyChip key="view" />
            ) : (
              <Button
                key="add"
                buttonText={t('create-new-site-area')}
                buttonType={ButtonTypes.primaryFilled}
                onClick={openSiteAreaCreateModal}
                leftIcon="add"
                leftIconClassName={materialSymbolsOutlined}
              />
            )
          }
        />
        {siteAreasQueryHookResult.firstLoading ? (
          <CircleLoader />
        ) : (
          <>
            {((!!siteAreasQueryHookResult.totalCount &&
              !!siteAreasQueryHookResult.collection.length) ||
              searchQuery) && (
              <Search placeholder={t('site-areas-search-placeholder')} />
            )}
            <Table
              actionsForSelected={tableActionsForSelected}
              actionsMenu={tableActionsMenu}
              columns={tableColumns}
              counterText={t('area-plural', {
                count: siteAreasQueryHookResult.totalCount,
              })}
              data={siteAreasQueryHookResult.collection}
              emptyState={emptyState}
              id="siteAreas"
              loading={siteAreasQueryHookResult.loading}
              totalEntries={siteAreasQueryHookResult.totalCount}
              useBulkSelectResult={useBulkSelectResult}
            />
          </>
        )}
      </WhiteBox>
    </DashboardContentMaxWidth>
  );
}

export default SiteAreas;
